import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import './LandingFeatures.css';
import LandingCard from '../LandingCard/LandingCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCircleXmark, 
  faArrowsRotate, 
  faClock, 
  faCartShopping 
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { faStar as faSparkles } from '@fortawesome/free-solid-svg-icons';

const features = [
  {
    icon: faCircleXmark,
    iconColor: 'var(--ovi-destructive)',
    problem: '¿Productos agotados en tu catálogo?',
    title: 'Se Agotó - Desapareció',
    description: 'Cuando se te agota un producto, simplemente lo inhabilitas y desaparece automáticamente del catálogo. Tus clientes solo ven lo que realmente tienes disponible.',
    benefit: 'Adiós a las llamadas de "no tengo ese producto"'
  },
  {
    icon: faWhatsapp,
    iconColor: 'var(--ovi-success)',
    problem: '¿Perder tiempo anotando pedidos?',
    title: 'Pedidos Directo a WhatsApp',
    description: 'Tus clientes eligen productos y te envían el pedido completo a tu WhatsApp con toda la información: productos, cantidades y datos del cliente.',
    benefit: 'Recibe pedidos organizados y listos para procesar'
  },
  {
    icon: faArrowsRotate,
    iconColor: 'var(--ovi-accent-cyan)',
    problem: '¿Crear catálogos cada vez que cambias algo?',
    title: 'Actualización Inteligente',
    description: 'Un solo catálogo que se actualiza automáticamente. Cambias precios, agregas productos o modificas descripciones y todo se sincroniza al instante.',
    benefit: 'Nunca más envíes versiones desactualizadas'
  },
  {
    icon: faClock,
    iconColor: 'var(--ovi-accent-purple)',
    problem: '¿Clientes preguntando disponibilidad?',
    title: 'Disponibilidad en Tiempo Real',
    description: 'Tus clientes siempre ven información actualizada. Si un producto no está disponible, automáticamente desaparece del catálogo.',
    benefit: 'Reduce consultas y acelera las ventas'
  },
  {
    icon: faCartShopping,
    iconColor: 'var(--ovi-accent-orange)',
    problem: '¿Catálogos complicados de usar?',
    title: 'Simple Para Cualquier Negocio',
    description: 'Da igual si vendes ropa, calzado, accesorios o cualquier producto. OviCatalogos se adapta a tu tipo de negocio sin complicaciones.',
    benefit: 'Perfecto para emprendedores y empresas'
  },
  {
    icon: faSparkles,
    problem: '¿Imagen poco profesional?',
    title: 'Catálogo Profesional',
    description: 'Muestra tus productos de forma elegante y organizada. Tus clientes verán tu negocio más profesional y confiable.',
    benefit: 'Impresiona y vende más con mejor presentación'
  }
];

function LandingFeatures() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const currentSection = sectionRef.current;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '-100px'
      }
    );

    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);

  return (
    <section className="ovicatalogos-features" ref={sectionRef}>
      <div className="ovicatalogos-features-gradient" />
      
      <div className="ovicatalogos-features-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="ovicatalogos-features-header"
        >
          <h2 className="ovicatalogos-features-title">
            Resuelve los problemas reales de tu negocio
          </h2>
          <p className="ovicatalogos-features-subtitle">
            OviCatalogos elimina los dolores de cabeza diarios que enfrentas al gestionar tus productos. 
            Cada característica está diseñada para resolver un problema específico.
          </p>
        </motion.div>

        <div className="ovicatalogos-features-grid">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`ovicatalogos-features-item ${isVisible ? 'ovicatalogos-features-item-visible' : ''}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <LandingCard>
                <LandingCard.Content>
                  <div className="ovicatalogos-features-icon-box">
                    <FontAwesomeIcon 
                      icon={feature.icon} 
                      className="ovicatalogos-features-icon"
                      style={{ color: feature.iconColor }}
                    />
                  </div>
                  
                  <div className="ovicatalogos-features-problem">
                    {feature.problem}
                  </div>
                  
                  <h3 className="ovicatalogos-features-card-title">
                    {feature.title}
                  </h3>
                  
                  <p className="ovicatalogos-features-description">
                    {feature.description}
                  </p>
                  
                  <div className="ovicatalogos-features-benefit">
                    <span className="ovicatalogos-features-benefit-check">✓</span>
                    <span className="ovicatalogos-features-benefit-text">{feature.benefit}</span>
                  </div>
                </LandingCard.Content>
              </LandingCard>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default LandingFeatures;
